import { useState } from "react";
import { Mail, MapPin, Clock, Instagram, Send } from "lucide-react";
import { NewsletterForm } from "@/components/NewsletterForm";
import { api, formatApiError } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { localBusinessSchema } from "@/lib/seo";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent - we'll be in touch shortly.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact & Visit Us - Green Street, London E13 9AP"
        description="Visit Zebaa at Unit 1, 312 Green Street, London, E13 9AP. Open Mon-Sat 10am-7pm, Sun 11am-5pm. South Asian imitation jewellery - bridal sets, kundan-style necklaces and gold-tone bangles in East London."
        path="/contact"
        jsonLd={localBusinessSchema()}
      />
      <div className="bg-surface py-16 text-center">
        <p className="text-xs uppercase tracking-[0.28em] text-pink">Get In Touch</p>
        <h1 className="font-serif text-5xl lg:text-6xl mt-3 text-charcoal">Visit Our Store</h1>
        <div className="w-16 h-px bg-gold mx-auto mt-6" />
      </div>

      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
          <div>
            <h2 className="font-serif text-3xl text-charcoal">Zebaa</h2>
            <p className="text-gray-600 mt-3 max-w-md leading-relaxed">
              Step into our shop in the heart of East London on Green Street - Zebaa is a
              South Asian imitation jewellery shop. Come and browse bridal sets, kundan-style
              necklaces, gold-tone bangles and chandbalis in person.
            </p>

            <div className="space-y-5 mt-8">
              <div className="flex items-start gap-4">
                <MapPin className="w-5 h-5 text-gold mt-0.5" />
                <div>
                  <h4 className="text-xs uppercase tracking-[0.15em] text-charcoal">Address</h4>
                  <p className="text-gray-600 mt-1">Unit 1, 312 Green Street, London, E13 9AP, United Kingdom</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Mail className="w-5 h-5 text-gold mt-0.5" />
                <div>
                  <h4 className="text-xs uppercase tracking-[0.15em] text-charcoal">Email</h4>
                  <p className="text-gray-600 mt-1">hello@zebaa.uk</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Instagram className="w-5 h-5 text-gold mt-0.5" />
                <div>
                  <h4 className="text-xs uppercase tracking-[0.15em] text-charcoal">Instagram</h4>
                  <p className="text-gray-600 mt-1">@zebaa.uk</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Clock className="w-5 h-5 text-gold mt-0.5" />
                <div>
                  <h4 className="text-xs uppercase tracking-[0.15em] text-charcoal">Opening Hours</h4>
                  <p className="text-gray-600 mt-1">Mon – Sat: 10am – 7pm · Sun: 11am – 5pm</p>
                </div>
              </div>
            </div>

            <div className="mt-10">
              <h4 className="text-xs uppercase tracking-[0.15em] text-charcoal mb-3">Join Our Newsletter</h4>
              <NewsletterForm variant="hero" />
            </div>
          </div>

          <div>
            <h2 className="font-serif text-3xl text-charcoal mb-6">Send Us a Message</h2>
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input required value={form.name} onChange={(e) => upd("name", e.target.value)} placeholder="Your Name"
                  data-testid="contact-name" className="w-full border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-pink" />
                <input required type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} placeholder="Your Email"
                  data-testid="contact-email" className="w-full border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-pink" />
              </div>
              <input value={form.subject} onChange={(e) => upd("subject", e.target.value)} placeholder="Subject (optional)"
                data-testid="contact-subject" className="w-full border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-pink" />
              <textarea required rows={5} value={form.message} onChange={(e) => upd("message", e.target.value)} placeholder="How can we help?"
                data-testid="contact-message" className="w-full border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-pink" />
              <button type="submit" disabled={loading} data-testid="contact-submit"
                className="flex items-center justify-center gap-2 w-full bg-pink text-white py-3.5 text-xs uppercase tracking-[0.18em] hover:bg-pink-hover transition-colors disabled:opacity-60">
                {loading ? "Sending..." : "Send Message"} <Send className="w-4 h-4" />
              </button>
            </form>

            <div className="mt-8 min-h-[300px]">
              <iframe
                title="Zebaa Location"
                src="https://www.google.com/maps?q=335+Green+Street,+London,+E13+9AR&output=embed"
                className="w-full h-full min-h-[300px] border-0 grayscale-[0.2]"
                loading="lazy"
                data-testid="contact-map"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
